import {LocalDbService} from "./LocalDBService.js";
import LogService from "services/LogService.js";
import {DbCollectionFactory} from "factories/DbCollectionFactory.js";
import {DB_COLLECTION_NAMES} from "constants/AppConstants.js";

let isAndroid: boolean = false;
if (typeof process.env.REACT_APP_FIREBASE_API_KEY !== "undefined") {
  // @ts-ignore -- /src is outside the node tsconfig program; resolved by the web bundler at runtime
  const module = await import("/src/FirebaseUtils.js");
  isAndroid = module.isAndroid;
}

let Directory: any = null;
let Filesystem: any = null;
let RecorderPlugin: any = null;
if (isAndroid) {
  const moduleCapacitor = await import("@capacitor/core");
  const capacitorFilesystem = await import("@capacitor/filesystem");

  Directory = capacitorFilesystem.Directory;
  Filesystem = capacitorFilesystem.Filesystem;
  RecorderPlugin = moduleCapacitor.registerPlugin("RecorderPlugin");
}

const convertSyncDataToPoints = (data: any): any => {
  const obj = data;
  obj.c = 0;
  obj.h = 0;

  return obj;
};

const convertSyncDataToNav = (data: any): any => {
  const obj = data;
  obj.Grp = "";
  obj.Prvy = 0;
  obj.WID = 0;
  obj.aE = "";
  obj.dsc = "";
  obj.aS = "";
  obj.brg = "";
  obj.dTL = 0;
  obj.eq = "";
  obj.eq2 = "";
  obj.eq3 = "";
  obj.fe = 0;
  obj.fnt = "android";
  obj.idF = data.ID;
  obj.jMa = 0;
  obj.jN = 0;
  obj.str = 0;
  obj.sv = true;
  obj.tck = 0;
  obj.isDel = false;
  obj.saveFirestore = false;

  return obj;
};

const getEquipmentForSport = async (userId: string, sportId: number): Promise<any[]> => {
  // Return empty array if userId or sportId is invalid
  if (!userId || sportId === undefined || sportId === null) {
    return [];
  }

  try {
    const equipmentFactory: any = await DbCollectionFactory.create(DB_COLLECTION_NAMES.equip);

    // Try to get equipment from Firebase with timeout protection
    const equipmentPromise = equipmentFactory.getAllEquipsByUser(userId, null, null, 1000, true, undefined);
    const timeoutPromise = new Promise((_, reject) =>
      setTimeout(() => reject(new Error("Equipment fetch timeout")), 5000)
    );

    const allEquipments = await Promise.race([equipmentPromise, timeoutPromise]);

    if (!allEquipments || typeof allEquipments !== "object") {
      LogService.addInfoLogAsync(userId, `No equipment data found for user`, false, true);
      return [];
    }

    // Filter equipment by sport and not deleted
    const sportEquipments: any[] = [];
    Object.keys(allEquipments).forEach((key) => {
      const equipment = allEquipments[key];
      if (equipment && equipment.del === 0 && equipment.sport === sportId) {
        sportEquipments.push({
          id: key,
          ...equipment
        });
      }
    });

    LogService.addInfoLogAsync(
      userId,
      `Found ${sportEquipments.length} equipment(s) for sport ${sportId}`,
      false,
      true
    );

    return sportEquipments;
  } catch (error) {
    // Log error but don't throw - return empty array to allow activity to save
    LogService.addErrorLogAsync(
      userId,
      `Error getting equipment for sport ${sportId}: ${error?.message || error}`,
      error,
      false,
      true
    );
    return [];
  }
};

const processSequentialBatches = async (
  items: any[],
  batchSize: number,
  processor: (item: any) => Promise<any>
): Promise<void> => {
  for (let i = 0; i < items.length; i += batchSize) {
    const batch = items.slice(i, i + batchSize);
    await Promise.all(batch.map(processor));
  }
};

export const syncLocalRecordingFiles = async (
  addNavigationsToState: ((nav: any, isLocal: boolean, isNew: boolean) => void) | null = null,
  userId: string,
  navCount: number
): Promise<void> => {
  if (!userId) {
    LogService.addInfoLogAsync("", "Sync not started no usr", false, true);
    return;
  }

  LogService.addInfoLogAsync(userId, "Sync local files started", false, true);

  let usr: string = userId;
  const equipmentCache = new Map<string, any[]>();
  const directoryPath = await RecorderPlugin.getActivitiesPath();
  if (directoryPath.path) {
    const res = await Filesystem.readdir({
      path: directoryPath.path
    });
    const files = res.files.map((entry) => entry.name);

    const CONCURRENCY = 3;
    return processSequentialBatches(files, CONCURRENCY, async (file) => {
      if ((!file.includes("demo_") && file.includes("_end")) || (file.includes("demo_") && navCount === 0)) {
        const filePath = "activities/" + file;

        try {
          const result = await Filesystem.readFile({
            path: filePath,
            directory: Directory.Data,
            encoding: "utf8"
          });

          if (result) {
            const dbService = await LocalDbService.getLocalDbService();

            const lines = result.data
              .trim()
              .split("\n")
              .map((line) => JSON.parse(line));

            if (lines.length > 0) {
              const nav = convertSyncDataToNav(lines[0]);
              if (nav.ID.includes("demo_")) {
                const now = Date.now() / 1000;
                const oneWeek = 7 * 24 * 60 * 60; // one week in milliseconds
                const twoWeeks = 2 * oneWeek; // two weeks in milliseconds
                const threeWeeks = 3 * oneWeek; // two weeks in milliseconds
                const fourWeeks = 4 * oneWeek; // two weeks in milliseconds

                // Sport comes from the activity's name, not its own "tp" field — the bundled
                // demo_windsurf_end.txt has a bad "tp": 0 (Other) baked in, so the name is the
                // more reliable source of truth here (mirrors the native Android seeder's fix).
                switch (true) {
                  case nav.nm?.toLowerCase().includes("sup"):
                    nav.dS = now - oneWeek;
                    nav.tp = 3;
                    break;
                  case nav.nm?.toLowerCase().includes("windsurf"):
                    nav.dS = now - twoWeeks;
                    nav.tp = 2;
                    break;
                  case nav.nm?.toLowerCase().includes("wing"):
                    nav.dS = now - threeWeeks;
                    nav.tp = 18;
                    break;
                  case nav.nm?.toLowerCase().includes("kayak"):
                    nav.dS = now - fourWeeks;
                    nav.tp = 5;
                    break;
                  default:
                    nav.dS = now;
                    nav.tp = 1;
                }
              }
              const points: any[] = [];
              for (let i = 1; i < lines.length; i++) {
                const line = lines[i];
                const point = convertSyncDataToPoints(line);
                points.push(point);
              }

              if (nav.usr) {
                usr = nav.usr;
              }

              LogService.addInfoLogAsync(usr, "Sync started for navId " + nav.ID, false, true);

              // Try to get equipment for the sport and assign to activity
              // This is wrapped in try-catch to ensure activity saves even if equipment fetch fails
              try {
                if (nav.tp !== undefined && nav.tp !== null) {
                  const cacheKey = `${usr}_${nav.tp}`;
                  if (!equipmentCache.has(cacheKey)) {
                    equipmentCache.set(cacheKey, await getEquipmentForSport(usr, nav.tp));
                  }
                  const sportEquipments = equipmentCache.get(cacheKey);
                  if (sportEquipments && sportEquipments.length > 0) {
                    // Assign up to 3 equipment items
                    if (sportEquipments[0] && sportEquipments[0].id) {
                      nav.eq = sportEquipments[0].id;
                    }
                    if (sportEquipments[1] && sportEquipments[1].id) {
                      nav.eq2 = sportEquipments[1].id;
                    }
                    if (sportEquipments[2] && sportEquipments[2].id) {
                      nav.eq3 = sportEquipments[2].id;
                    }
                    LogService.addInfoLogAsync(
                      usr,
                      `Assigned ${sportEquipments.length} equipment(s) to activity ${nav.ID} for sport ${nav.tp}`,
                      false,
                      true
                    );
                  } else {
                    LogService.addInfoLogAsync(
                      usr,
                      `No equipment found for activity ${nav.ID} sport ${nav.tp}`,
                      false,
                      true
                    );
                  }
                }
              } catch (equipError) {
                // Log error but continue with activity save
                LogService.addErrorLogAsync(
                  usr,
                  `Failed to assign equipment to activity ${nav.ID}: ${equipError?.message || equipError}`,
                  equipError,
                  false,
                  true
                );
                // Continue with saving the activity without equipment
              }

              if (points.length > 0) {
                if (await dbService.setPointsForNavId(nav.ID, points)) {
                  if (await dbService.setNavigation(nav)) {
                    addNavigationsToState?.(nav, true, true);
                    await Filesystem.deleteFile({
                      path: filePath,
                      directory: Directory.Data
                    });
                    LogService.addInfoLogAsync(usr, "Sync saved for navId " + nav.ID, false, true);
                  } else {
                    LogService.addInfoLogAsync(usr, "Error saving navigation " + nav.ID, false, true);
                  }
                } else {
                  LogService.addInfoLogAsync(usr, "Error saving points " + nav.ID, false, true);
                }
              } else {
                LogService.addInfoLogAsync(
                  usr,
                  "Error sync local file no points: " + JSON.stringify(lines[0]),
                  false,
                  true
                );
                Filesystem.deleteFile({
                  path: filePath,
                  directory: Directory.Data
                });
              }
            } else {
              LogService.addInfoLogAsync(usr, `Error sync local file empty ${filePath}`, false, true);
              Filesystem.deleteFile({
                path: filePath,
                directory: Directory.Data
              });
            }
          } else {
            LogService.addInfoLogAsync(usr, `Error sync local no file ${filePath}`, false, true);
          }
        } catch (error) {
          LogService.addInfoLogAsync(usr, "Error sync local file error: " + error, false, true);
          Filesystem.deleteFile({
            path: filePath,
            directory: Directory.Data
          });
        }
      }
    });
  } else {
    LogService.addInfoLogAsync(usr, "Sync local files error no path", false, true);
  }
};
